// src/pages/LoginPage.tsx
import React, { useState } from 'react';
import { Card, Form, Input, Button, Checkbox, message, Typography } from 'antd';
import { UserOutlined, LockOutlined, EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { authAPI } from '../utils/api';

const { Title, Text } = Typography;

export const LoginPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const onFinish = async (values: any) => {
    try {
      setLoading(true);
      const response = await authAPI.login({
        email: String(values.email || '').trim().toLowerCase(),
        password: values.password,
      });
      const responseData = response.data as { access_token: string; user: any };
      if (!responseData?.access_token || !responseData?.user) {
        message.error('Некорректный ответ сервера. Попробуйте снова.');
        return;
      }
      login(responseData);
      message.success('Успешный вход в систему!');

      // Redirect based on user role
      if (responseData.user?.role === 'admin') {
        navigate('/admin');
      } else if (responseData.user?.role === 'doctor') {
        navigate('/doctors');
      } else {
        navigate('/dashboard');
      }
    } catch (error: any) {
      console.error('Login error:', error);
      message.error(error.response?.data?.detail || 'Ошибка входа. Проверьте данные и попробуйте снова.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: '80vh',
      backgroundColor: '#F0F9F4',
      padding: '20px'
    }}>
      <Card 
        style={{ 
          width: '100%', 
          maxWidth: 400,
          borderRadius: '12px',
          boxShadow: '0 6px 16px rgba(0,0,0,0.08)'
        }}
        title={
          <div style={{ textAlign: 'center' }}>
            <Title level={2} style={{ color: '#263238', marginBottom: 0 }}>Добро пожаловать</Title>
            <Text type="secondary">Войдите в свой аккаунт</Text>
          </div>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
        >
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: 'Пожалуйста, введите email' },
              { type: 'email', message: 'Введите корректный email' }
            ]}
          >
            <Input 
              prefix={<UserOutlined />} 
              placeholder="your.email@example.com"
              size="large"
            />
          </Form.Item>

          <Form.Item
            label="Пароль"
            name="password"
            rules={[{ required: true, message: 'Пожалуйста, введите пароль' }]}
          >
            <Input.Password 
              prefix={<LockOutlined />} 
              placeholder="Введите пароль"
              size="large"
              iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
            />
          </Form.Item>

          <Form.Item>
            <Form.Item name="remember" valuePropName="checked" noStyle>
              <Checkbox>Запомнить меня</Checkbox>
            </Form.Item>

            <Button type="link" style={{ float: 'right', padding: 0 }} onClick={() => navigate('/forgot-password')}>
              Забыли пароль?
            </Button>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              loading={loading}
              style={{ 
                width: '100%', 
                backgroundColor: '#7CB342',
                borderColor: '#7CB342',
                height: '48px',
                fontSize: '16px',
                fontWeight: '500'
              }}
            >
              Войти
            </Button>
          </Form.Item>
        </Form>

        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <Text type="secondary">
            Нет аккаунта? <Link to="/register">Зарегистрироваться</Link>
          </Text>
          <div style={{ marginTop: 8 }}>
            <Text type="secondary">
              Продолжая, вы соглашаетесь с{' '}
              <Link to="/privacy-policy">Политикой конфиденциальности</Link>
            </Text>
          </div>
        </div>
      </Card>
    </div>
  );
};
